import { useCallback } from "react";
import { graphql, useMutation } from "react-relay/hooks";

import type {
  LoginTypesUpdate,
  organizationpermission,
  ResourceGroupLinkInput,
  upsertUserMutation,
} from "__generated__/upsertUserMutation.graphql";

export const userUpsertMutation = graphql`
  mutation upsertUserMutation(
    $email: String
    $nodeId: ID
    $firstName: String!
    $lastName: String!
    $phoneNumber: String
    $organizationPermission: organizationpermission!
    $organizationNotes: String!
    $resourceGroupLinks: [ResourceGroupLinkInput]
    $overrideLoginTypes: Boolean
    $loginTypes: [LoginTypesUpdate]
    $expiry: DateTime
  ) {
    upsertUser(
      email: $email
      nodeId: $nodeId
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
      organizationPermission: $organizationPermission
      organizationNotes: $organizationNotes
      resourceGroupLinks: $resourceGroupLinks
      overrideLoginTypes: $overrideLoginTypes
      loginTypes: $loginTypes
      expiry: $expiry
    ) {
      user {
        id
        email
        firstName
        lastName
        phoneNumber
        displayName
        expiry
        organizationPermission
        resourceGroupLinks {
          edges {
            node {
              id
              resourceGroupId
              resourceGroupName
              resourceGroupPermission
            }
          }
        }
        supportedLoginOptions {
          loginType
          identityProviderConfiguration {
            name
            providerType
          }
        }
        organizationNotes
        loginTypesOverriden
        loginTypes {
          edges {
            node {
              authEnabled
              loginType
              identityProviderConfiguration {
                uuid
                name
              }
            }
          }
        }
      }
      errors
    }
  }
`;
export const useUpsertUser = (onCompleted?: (response: upsertUserMutation["response"]) => void) => {
  // NOTE(david.taylor): upsert user considered harmful, don't use this mutation anywhere other than
  // the user profile modal.
  const [upsertUser, loading] = useMutation<upsertUserMutation>(userUpsertMutation);
  const callback = useCallback(
    (
      email: string | null,
      nodeId: string | null,
      firstName: string,
      lastName: string,
      phoneNumber: string | null,
      organizationPermission: organizationpermission,
      organizationNotes: string,
      resourceGroupLinks: ResourceGroupLinkInput[],
      overrideLoginTypes: boolean | null,
      loginTypes: LoginTypesUpdate[] | null,
      expiry: string | null
    ) => {
      upsertUser({
        variables: {
          email,
          nodeId,
          firstName,
          lastName,
          phoneNumber,
          organizationPermission,
          organizationNotes,
          resourceGroupLinks,
          overrideLoginTypes,
          loginTypes,
          expiry,
        },
        onCompleted,
      });
    },
    [onCompleted, upsertUser]
  );
  return [callback, loading] as [typeof callback, boolean];
};
